/**
 * The one way the app talks to the server (A2). Every path, body and answer is
 * typed from `schema.d.ts`, which is generated from `contract/openapi.json` —
 * so a renamed field fails `tsc` here instead of blanking a screen.
 */
import createClient from "openapi-fetch";

import { callText, note } from "../app/trail";
import type { components, paths } from "./schema";

export type Schemas = components["schemas"];

export const api = createClient<paths>({
  // The page's own origin, said aloud: Node's `fetch` under a test has no page
  // to resolve a bare `/api/…` against.
  baseUrl: typeof location === "undefined" ? "" : location.origin,
  // Looked up at each call, not kept from load, so MSW's stand-in is the one used.
  fetch: (request) => globalThis.fetch(request),
});

/**
 * Every request says the person's zone (D22): the server's day — Today,
 * overdue, a habit's state — is theirs, not the install's. Read at each call,
 * so a phone that crosses a border is in the new zone at its next request.
 * Found building 27A: no request sent it, so every day was London's.
 */
export function zone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

api.use({
  onRequest({ request }) {
    if (!request.headers.has("X-Time-Zone")) request.headers.set("X-Time-Zone", zone());
    return request;
  },
});

/** Each call's start, for its time in the action log (`issue_reports.md` rule 4). */
const started = new Map<string, number>();

api.use({
  onRequest({ id }) {
    started.set(id, performance.now());
  },
  onResponse({ id, request, response }) {
    const ms = performance.now() - (started.get(id) ?? performance.now());
    started.delete(id);
    note("call", callText(request.method, request.url, response.status, ms));
  },
  onError({ id, request }) {
    const ms = performance.now() - (started.get(id) ?? performance.now());
    started.delete(id);
    note("call", callText(request.method, request.url, "no answer", ms));
  },
});

/** Every event the live stream carries, by its SSE name (`core/notify/events.py`);
 * the shell hears them (`shell/live.ts`). */
export type LiveEvents = Schemas["LiveEvents"];
export type LiveEventName = keyof LiveEvents;

/**
 * A file as the multipart body a route takes, under `file` (27B: a pasted
 * picture, a tablet upload). openapi-fetch sends `FormData` as it is; the
 * contract types the field as a string, so the form is cast to the body.
 */
export function fileForm<Body>(file: Blob, name?: string): Body {
  const form = new FormData();
  form.append("file", file, name);
  return form as unknown as Body;
}
